import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { useDatasets } from '@/api/generated';
import { DataTable } from '@/components/blocks/data-table/data-table';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useOpen } from '@/hooks/use-open';
import CreateDatasetDialog from '@/routes/(app)/(datasets)/components/create-dataset-dialog';
import DatasetExpansion from '@/routes/(app)/(datasets)/components/dataset-expansion';
import { columns } from '@/routes/(app)/(datasets)/lib/datasets-columns';

const Datasets = () => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { open, onOpenChange } = useOpen({
    defaultOpen: false,
  });

  const handleOpenCreateDataset = useCallback(() => {
    onOpenChange(true);
  }, [onOpenChange]);

  const { data, isLoading, refetch } = useDatasets({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    order: sorting[0]?.desc ? 'desc' : 'asc',
    orderBy: sorting[0]?.id,
  });

  const table = useReactTable({
    state: {
      pagination,
      sorting,
      expanded,
    },
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    pageCount: Math.ceil((data?.total ?? 0) / pagination.pageSize),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    manualSorting: true,
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getRowCanExpand: (row) => row.original._count.files > 0,
  });

  return (
    <>
      <div className='flex justify-between gap-4'>
        <div className='flex flex-col gap-2'>
          <h1 className='text-4xl font-bold'>{t('datasets.title')}</h1>
          <p className='text-muted-foreground'>{t('datasets.description')}</p>
        </div>
        <div className='flex items-end justify-end gap-4'>
          <Button onClick={handleOpenCreateDataset}>
            {t('datasets.createDataset.button')}
          </Button>
        </div>
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable
          table={table}
          Expansion={DatasetExpansion}
        />
      )}

      <CreateDatasetDialog
        open={open}
        onOpenChange={onOpenChange}
        refetch={refetch}
      />
    </>
  );
};

export default Datasets;
